import { useRef, useState } from 'react'
import { GeldFeldFuer, geldEingabe } from '@/components/geldfeld'
import { Vorschlaege, useSuche } from '@/components/zeilenblock'
import { FeldStatus, useFeld, type Speicherstelle } from '@/lib/speichern'

/**
 * **Felder direkt in einer Listenzelle** (Auftraggeber 13.10.2026): In den Listen „Materialanfragen" (Kalkulation)
 * und „Einkauf" (Projektmanagement) lassen sich Einkaufspreis und Lieferant ändern, ohne die Zeile in ihrem Blatt
 * bzw. ihrer Stückliste zu öffnen — etwa wenn ein Lieferant seinen Preis am Telefon nennt.
 *
 * Die Felder stehen immer da (`.claude/rules/speichern.md`: kein Lesemodus mit Stift) und lesen sich wie der Text
 * der Liste; der Rahmen erscheint unter dem Zeiger und beim Bearbeiten. Gespeichert wird über den Speicherbaustein
 * beim Verlassen des Felds bzw. mit Enter, Esc stellt den alten Wert her. Die Speicherstelle baut die Liste je
 * Zeile — mit demselben Schlüssel wie die Maske, in der die Zeile sonst bearbeitet wird.
 *
 * Wo eine Zeile nicht geändert werden darf (bestellt, abgegebene Kalkulation, ohne Recht), zeigt die Liste wie
 * bisher Text und setzt keines dieser Felder ein.
 */

/** Ohne Rahmen und Fläche, bis man hinzeigt oder hineinklickt. */
const DIREKT =
  'w-full min-w-0 rounded border border-transparent bg-transparent px-1.5 py-0.5 text-sm hover:border-border focus:border-ring focus:bg-background focus:outline-none aria-[invalid=true]:border-destructive'

/** Ein Preis in einer Zelle. `stellen`: Nachkommastellen eines Einzelpreises (Vorgabe 2). */
export function DirektPreis({ stelle, feld, label, stellen = 2, breite = 'w-24' }: { stelle: Speicherstelle<unknown>; feld: string; label: string; stellen?: number; breite?: string }) {
  const f = useFeld<number | null>(stelle, feld, { art: 'zahl', label, schreib: (w) => geldEingabe(w, stellen) })
  return (
    <span className="inline-block text-right align-middle">
      <span className="inline-flex items-center gap-1">
        <FeldStatus status={f} />
        <GeldFeldFuer feld={f.props} stellen={stellen} className={`${DIREKT} ${breite}`} aria-label={label} title={`${label} ändern — gespeichert wird beim Verlassen des Felds`} placeholder="—" />
      </span>
    </span>
  )
}

/**
 * Der Lieferant einer Zeile: Im Feld steht der Name, gespeichert wird die Kennung. Ein getippter, aber nicht
 * gewählter Name ändert nichts — das Feld zeigt dann wieder den gespeicherten Lieferanten. Ein geleertes Feld
 * löst ihn.
 */
export function DirektLieferant({
  stelle,
  feld = 'supplierId',
  name,
  suche,
  label = 'Lieferant',
}: {
  stelle: Speicherstelle<unknown>
  feld?: string
  /** Name des gespeicherten Lieferanten — die Stelle kennt nur seine Kennung. */
  name: string | null
  suche: (q: string) => Promise<{ id: string; name: string }[]>
  label?: string
}) {
  // Welche Kennung zu welchem Namen gehört, weiß nur die Oberfläche: aus der Zeile und aus der Suche.
  const namen = useRef(new Map<string, string>())
  const gespeichert = (stelle.basis(feld) as string | null) ?? null
  if (gespeichert && name) namen.current.set(gespeichert, name)
  const f = useFeld<string | null>(stelle, feld, {
    label,
    schreib: (id) => (id ? (namen.current.get(id) ?? '') : ''),
    lies: (text) => {
      const t = text.trim()
      if (t === '') return null
      for (const [id, n] of namen.current) if (n === t) return id
      return (stelle.basis(feld) as string | null) ?? null
    },
  })
  const [sucht, setSucht] = useState(false)
  const [treffer, setTreffer] = useSuche<{ id: string; name: string }>(f.props.value, sucht, suche)
  const schliessen = () => {
    setSucht(false)
    setTreffer([])
  }
  return (
    <span className="relative block min-w-[9rem]">
      <input
        className={DIREKT}
        autoComplete="off"
        aria-label={label}
        title={`${label} ändern — Name tippen und aus der Liste wählen`}
        placeholder="—"
        {...f.props}
        onChange={(e) => {
          f.props.onChange(e)
          setSucht(true)
        }}
        onBlur={() => {
          f.props.onBlur()
          schliessen()
        }}
      />
      <Vorschlaege
        allein
        fest
        eintraege={treffer}
        schluessel={(l) => l.id}
        onWahl={(l) => {
          namen.current.set(l.id, l.name)
          f.setze(l.id)
          schliessen()
        }}
        darstellung={(l) => l.name}
      />
      <FeldStatus status={f} />
    </span>
  )
}
